import type { definitions } from '@luxuryescapes/contract-svc-lab-v2'
import type { LabV2Ui } from './evaluation'
import { removeRegionCode } from 'lib/string/regionUtils'
import UrlLocation from 'src/lib/url/UrlLocation'

type HeaderBannerUi = Extract<LabV2Ui, { type: 'header_banner' }>

export type LabV2HeaderBanner = {
  id: number
  triggerPoints: HeaderBannerUi['trigger_points']
  kind: definitions['HeaderBannerPayload']['kind']
  isCustomAppearance: boolean
  textColor: string
  ctaPrimaryTextColor: string
  description: definitions['HeaderBannerDescription']
  background: definitions['HeaderBannerBackground']
  cta?: definitions['HeaderBannerPayload']['cta']
  iconImageId?: string
}

export function headerBannerDismissalId(banner: Pick<LabV2HeaderBanner, 'id'>): string {
  return `v2-header-${banner.id}`
}

export function headerBannersFromUis(uis: Array<LabV2Ui> | undefined): Array<LabV2HeaderBanner> {
  if (!uis) {
    return []
  }

  const banners: Array<LabV2HeaderBanner> = []
  for (const ui of uis) {
    if (ui.type !== 'header_banner') {
      continue
    }
    banners.push({
      id: ui.id,
      triggerPoints: ui.trigger_points,
      kind: ui.payload.kind,
      isCustomAppearance: ui.payload.isCustomAppearance,
      textColor: ui.payload.textColor,
      ctaPrimaryTextColor: ui.payload.ctaPrimaryTextColor,
      description: ui.payload.description,
      background: ui.payload.background,
      cta: ui.payload.cta,
      iconImageId: ui.payload.iconImageId,
    })
  }
  return banners
}

/**
 * Evaluate already returns qualifying UI rows in campaign priority order.
 * This keeps that order and drops banners for other pages.
 */
export function headerBannersForPage(
  banners: Array<LabV2HeaderBanner>,
  path: string,
  dismissedIds: Set<string>,
  regionCode?: string,
): Array<LabV2HeaderBanner> {
  const triggerPoint = triggerPointForPath(path, regionCode)
  if (!triggerPoint) {
    return []
  }

  return banners.filter(banner =>
    banner.triggerPoints.includes(triggerPoint) &&
    !dismissedIds.has(headerBannerDismissalId(banner)),
  )
}

export function triggerPointForPath(
  path: string,
  regionCode?: string,
): LabV2HeaderBanner['triggerPoints'][number] | undefined {
  const withoutLocale = new UrlLocation(path).withoutLocale().path
  const pagePath = regionCode ? removeRegionCode(withoutLocale, regionCode) : withoutLocale
  const normalized = pagePath.startsWith('/') ? pagePath : `/${pagePath}`
  if (normalized === '/') {
    return 'home'
  }
  if (normalized === '/search' || normalized.startsWith('/search/')) {
    return 'search_results'
  }
  if (normalized.includes('/offer/')) {
    return 'offer_detail'
  }
  if (normalized === '/cart' || normalized.startsWith('/cart/')) {
    return 'cart'
  }
  if (normalized === '/checkout' || normalized.startsWith('/checkout/')) {
    return 'checkout'
  }
  if (normalized.startsWith('/account/my-escapes')) {
    return 'my_escapes'
  }
  if (normalized.startsWith('/account/vouchers')) {
    return 'voucher_wallet'
  }
  const verticals = ['/hotels', '/tours', '/cruises', '/experiences', '/flights', '/car-hire', '/homes-and-villas', '/ultra-lux']
  if (verticals.some(vertical => normalized === vertical || normalized.startsWith(`${vertical}/`))) {
    return 'vertical_landing'
  }
  if (normalized.startsWith('/destination') || normalized.startsWith('/campaign')) {
    return 'campaign_or_destination'
  }
  return undefined
}
